import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { SidebarNotificationBadge } from 'src/components/sidebar/sidebar-items/SidebarNotificationBadge';
import * as Constants from 'src/constants';
import {
  API_SETTINGS_PAGE,
  APP_SETUP_PAGE,
  ASSISTANT_SETTINGS_PAGE,
  AUTH_SETTINGS_PAGE,
  BILLING_PAGE,
  CLIENT_NOTIFICATIONS_PAGE,
  CONNECTIONS_PAGE,
  CUSTOMIZATION_PAGE,
  DOMAIN_PAGE,
  NOTIFICATIONS_SETTINGS_PAGE,
  PAYMENTS_PAGE,
  PLAN_PAGE,
  PORTAL_ITEM,
  PROFILE_PAGE,
  SETTINGS_EVENTS_PAGE,
} from 'src/constants';
import { hasAppRouteAccess } from 'src/constants/appPermissions';
import {
  PortalConfigContext,
  RouteContext,
  useFeatureFlags,
} from 'src/context';
import { useEnableNewSidebar } from 'src/hooks/useEnableNewSidebar';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useSidebarNotificationCounts } from 'src/hooks/useSidebarNotificationCounts/useSidebarNotificationCounts';
import { useAppSelector } from 'src/hooks/useStore';
import { useUserAccess } from 'src/hooks/useUserAccess';
import { useUserCanInvite } from 'src/hooks/useUserCanInvite';
import { SETTINGS_AUTOMATIONS_PATH } from 'src/legacy/components/automations/useAutomationsBasePath';
import { ContractPageTabs } from 'src/legacy/components/Contracts';
import { ExtensionTooltipTitle } from 'src/legacy/components/Tooltip';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useGetPortalConfigQuery } from 'src/services/api';
import { useListAppBuildersQuery } from 'src/services/api/appBuilderApi';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetProductStoresQuery } from 'src/services/api/productStoreApi';
import { RootState } from 'src/store';
import { getBuilderSessionPath, isDraftApp } from 'src/utils/ExtensionUtils';
import { getModuleIcon, getModuleName } from 'src/utils/ModuleSettingsUtils';

import { DraftBadge } from './DraftBadge';
import { nestItemsInFolders } from './nestItemsInFolders';
import { InboxNotificationBadge } from './NotificationBadges';
import { NavItem, NavSection } from './SidebarNav';
import {
  isFilesNavItemSelected,
  isGeneralSettingsNavItemSelected,
} from './sidebarSelection';
import { UpgradeBadge } from './UpgradeBadge';

interface CoreModulesNavItems {
  /**
   * Core modules without folders (no nested nav items except for billing).
   * This provides compatibility with existing useSidebarItems usage
   */
  navItems: NavItem[];
  /** Core modules with nested folder nav items */
  navItemsWithFolders?: NavItem[];
}

export const appNavItemIdPrefix = 'sidebar-app-';
export const moduleNavItemIdPrefix = 'sidebar-';

enum ExtensionItemViewLocation {
  IN_PORTAL = 'in-portal',
  NEW_TAB = 'new-tab',
}

export type SidebarType = 'main' | 'settings';

export interface UseSidebarItemsResult {
  sections: Array<NavSection>;
  isLoadingInstalls: boolean;
}

type ClientExperienceItemsRouteAccess = Pick<
  ReturnType<typeof useRouteAccess>['routeAccess'],
  | 'allowSettingsClientAuth'
  | 'allowSettingsClientNotificationsPage'
  | 'allowSettingsDomainPage'
>;

type ClientExperienceItemsPlanStatus = Pick<
  ReturnType<typeof usePlanStatus>,
  'isStarterPlan' | 'requireProUpgrade'
>;

export const getClientExperienceItems = ({
  routeAccess,
  pathname,
  planStatus,
}: {
  routeAccess: ClientExperienceItemsRouteAccess;
  pathname: string;
  planStatus: ClientExperienceItemsPlanStatus;
}): Array<NavItem> => [
  {
    htmlId: 'auth-settings-page',
    label: AUTH_SETTINGS_PAGE.label,
    path: AUTH_SETTINGS_PAGE.path,
    icon: AUTH_SETTINGS_PAGE.icon,
    canAccess: routeAccess.allowSettingsClientAuth,
    selected: pathname.includes(AUTH_SETTINGS_PAGE.path),
    iconName: 'lock',
  },
  {
    htmlId: 'client-notifications-page',
    label: CLIENT_NOTIFICATIONS_PAGE.label,
    path: CLIENT_NOTIFICATIONS_PAGE.path,
    icon: CLIENT_NOTIFICATIONS_PAGE.icon,
    iconName: 'bell',
    canAccess: routeAccess.allowSettingsClientNotificationsPage,
    selected: pathname.includes(CLIENT_NOTIFICATIONS_PAGE.path),
  },
  {
    htmlId: 'domain-setting-page',
    label: DOMAIN_PAGE.label,
    path: DOMAIN_PAGE.path,
    icon: DOMAIN_PAGE.icon,
    iconName: 'globe',
    canAccess: routeAccess.allowSettingsDomainPage,
    children: (
      <UpgradeBadge
        show={planStatus.isStarterPlan || planStatus.requireProUpgrade}
      />
    ),
  },
];

/**
 * Whether an extension should appear in the sidebar. Clients only see connected
 * global/local extensions that aren't drafts. Internal users see drafts only when
 * they can open the builder — without builder access a draft nav item has no
 * builderId and routes to an empty extension page.
 */
export const isExtensionVisibleInSidebar = ({
  isClient,
  extensionId,
  extensionConfig,
  setupExtensionConfigSet,
  canAccessBuilder,
}: {
  isClient: boolean;
  extensionId: string;
  extensionConfig: Constants.ExtensionConfig;
  setupExtensionConfigSet: Record<string, boolean>;
  canAccessBuilder: boolean;
}): boolean =>
  isClient
    ? (['global'].includes(extensionConfig.type) ||
        !!setupExtensionConfigSet[extensionId]) &&
      !extensionConfig.isDraft
    : canAccessBuilder || !extensionConfig.isDraft;

export const useSidebarItems = (
  sidebarType?: SidebarType,
  opts: { shouldPreviewAsClient?: boolean } = {},
): UseSidebarItemsResult => {
  const { shouldPreviewAsClient = false } = opts;
  const { t } = useTranslation();
  const { routeAccess } = useRouteAccess({ shouldPreviewAsClient });
  const { pathname, query } = useContext(RouteContext);
  const planStatus = usePlanStatus();
  const { isInviteConfigDisabled } = useUserCanInvite();
  const companiesDisabled =
    !!useContext(PortalConfigContext).features.disableCompanies;
  const {
    EnableManyCompanies,
    EnableIUAccessControl,
    EnableAuditLogs,
    EnableAppBuilder,
    EnableStudioSidebar,
  } = useFeatureFlags();
  const enableNewSidebar = useEnableNewSidebar();
  // check if logged in user has permission to adjust internal role
  // and invite config is not disabled
  const hasModifyInternalRolePermission =
    !isInviteConfigDisabled &&
    useUserAccess(
      Constants.PERM_MOD_SETTINGS_MEMBERS,
      Constants.USER_OPERATIONS.AdjustInternalRole,
    );
  const activeCompanyId = useAppSelector(
    (state: RootState) => state.activeCompany.activeCompanyId,
  );
  const {
    data: { moduleSettings, extensionsSettings } = {},
    isLoading: isLoadingInstalls,
  } = useGetInstallsQuery(shouldPreviewAsClient ? 'preview' : undefined);
  const { id: queryIdParam } = query;
  const { isClient, extensionItems, lastVisitedCrmTabRoute } = useAppSelector(
    (state: RootState) => ({
      extensionItems: state.dashboard.items,
      isClient: state.user.isClient || shouldPreviewAsClient === true,
      lastVisitedCrmTabRoute: state.clients.lastVisitedTab,
    }),
    shallowEqual,
  );
  const hasAutomationsAccess = useUserAccess(
    Constants.PERM_MOD_AUTOMATIONS,
    '',
  );
  const { data: portalConfigurations } = useGetPortalConfigQuery();
  const shouldFilterNonClientItems =
    isClient && portalConfigurations?.structFields?.disableCompanies;

  const canAccessBuilder = routeAccess.allowAppSetupPage;
  const { data: appBuilders } = useListAppBuildersQuery(undefined, {
    skip: !EnableAppBuilder || !canAccessBuilder,
  });
  const builderIdByModuleId = React.useMemo(() => {
    const map = new Map<string, string>();
    appBuilders?.forEach((builder) => {
      if (builder.installedModuleId) {
        map.set(builder.installedModuleId, builder.id);
      }
    });
    return map;
  }, [appBuilders]);

  const setupExtensionConfigSet = extensionItems
    .filter((extensionItem) => {
      const isActiveCompanyMatch = extensionItem.fields.companyID
        ? extensionItem.fields.companyID === activeCompanyId
        : true;
      const isCompanyMatch = !EnableManyCompanies ? true : isActiveCompanyMatch;
      return isCompanyMatch && extensionItem.fields.status === 'connected';
    })
    .reduce(
      (extensionConfigSet: Record<string, boolean>, extensionItem) => ({
        ...extensionConfigSet,
        [extensionItem.fields.extensionConfigId || 'default']: true,
      }),
      {},
    );
  const { data: stores = [] } = useGetProductStoresQuery();
  const store = stores.at(0);

  const meSectionNavItems: Array<NavItem> = [
    {
      htmlId: 'account-setting-page',
      label: PROFILE_PAGE.label,
      path: PROFILE_PAGE.path,
      icon: PROFILE_PAGE.icon,
      canAccess: true,
      iconName: 'user',
    },

    {
      htmlId: 'notification-setting-page',
      label: NOTIFICATIONS_SETTINGS_PAGE.label,
      path: NOTIFICATIONS_SETTINGS_PAGE.path,
      icon: NOTIFICATIONS_SETTINGS_PAGE.icon,
      iconName: 'bell',
      canAccess: routeAccess.allowNotificationSettings,
    },
  ];

  const { messages, files, invoices, forms, contracts, apps } =
    useSidebarNotificationCounts();
  const { backgroundColor, textColor } = useNotificationBadgeData(true);

  /**
   * This function calculate for each extension sidebar item
   * the external link url and the extension view location (in portal or new tab).
   *
   * @param extensionConfig
   * @param extId
   * @returns
   */
  const getExtensionLinkAccessibilityData = (
    extensionConfig: Constants.ExtensionConfig,
    extId: string,
  ) => {
    const defaultExtensionPath = `${Constants.CLIENT_APPS_PAGE.path}?id=${extId}`;
    let extensionItemAccessibilityData = {
      extensionLink: defaultExtensionPath,
      viewExtensionLocation: ExtensionItemViewLocation.IN_PORTAL,
    };
    if (extensionConfig.type === 'local') {
      // find all local connected extension channels that match this extensionID
      let connectedLocalExtensionItems = extensionItems.filter(
        (extensionItem) =>
          extensionItem.fields.extensionConfigId === extId &&
          extensionItem.fields.status === 'connected' &&
          extensionItem.fields.companyID === activeCompanyId,
      );
      if (EnableManyCompanies) {
        connectedLocalExtensionItems = connectedLocalExtensionItems.filter(
          (extensionItem) => extensionItem.fields.companyID === activeCompanyId,
        );
      }

      const connectedLocalExtensionItem =
        connectedLocalExtensionItems.length > 0
          ? connectedLocalExtensionItems.at(0)
          : null;

      if (connectedLocalExtensionItem) {
        const hasMoreThanOneConnectedEmbed =
          connectedLocalExtensionItems.length > 1;
        // default view local extensions in portal
        let viewExtensionLocation = ExtensionItemViewLocation.IN_PORTAL;
        // when client is viewing extension with embedLink and only has one channel then view in new tab
        if (
          isClient &&
          !hasMoreThanOneConnectedEmbed &&
          connectedLocalExtensionItem.fields.embedLink
        ) {
          viewExtensionLocation = ExtensionItemViewLocation.NEW_TAB;
        }
        extensionItemAccessibilityData = {
          ...extensionItemAccessibilityData,
          extensionLink:
            isClient &&
            connectedLocalExtensionItem.fields.embedLink &&
            !hasMoreThanOneConnectedEmbed
              ? connectedLocalExtensionItem.fields.embedLink
              : defaultExtensionPath,
          viewExtensionLocation,
        };
      }
    } else {
      // global extension
      extensionItemAccessibilityData = {
        ...extensionItemAccessibilityData,
        extensionLink:
          isClient && extensionConfig.embedLink
            ? extensionConfig.embedLink
            : defaultExtensionPath,
        viewExtensionLocation:
          isClient && extensionConfig.embedLink
            ? ExtensionItemViewLocation.NEW_TAB
            : ExtensionItemViewLocation.IN_PORTAL,
      };
    }

    return extensionItemAccessibilityData;
  };

  /**
   * This function calculates the CRM path based on the user's last visited crm tab.
   * @returns CRM path based on the user's last visited crm tab
   */
  const getCrmPath = () => {
    if (!companiesDisabled) {
      // check if there is a last visited crm path in local storage
      return lastVisitedCrmTabRoute || Constants.COMPANIES_PAGE.path;
    }

    return Constants.CRM_CLIENTS_PAGE.path;
  };

  const extensionNavItems: NavItem[] = routeAccess.allowExtensionsPage
    ? Object.entries(extensionsSettings || {})
        .filter(([extensionID]) =>
          (moduleSettings || []).some((module) => extensionID === module.id),
        )
        .filter(([extensionID, extensionConfig]) =>
          isExtensionVisibleInSidebar({
            isClient,
            extensionId: extensionID,
            extensionConfig,
            setupExtensionConfigSet,
            canAccessBuilder,
          }),
        )
        .filter(([, extensionConfig]) => {
          // Filter extensions by routeAccess (when flag enabled)
          if (!EnableIUAccessControl) return true; // feature flag not enabled

          const { appId } = extensionConfig;
          if (!appId) return true; // no app ID means no routeAccess requirement

          return hasAppRouteAccess(appId, routeAccess);
        })
        .sort(
          ([, extensionConfigA], [, extensionConfigB]) =>
            // first order by extension type so global is before local
            // then by `sort` which is number indicating position in the sidebar
            extensionConfigA.type.localeCompare(extensionConfigB.type) ||
            extensionConfigA.sort - extensionConfigB.sort,
        )
        .map(([extensionId, extensionConfig]) => {
          const { extensionLink: extensionItemPath, viewExtensionLocation } =
            getExtensionLinkAccessibilityData(extensionConfig, extensionId);

          const notificationCount = extensionConfig.appId
            ? (apps[extensionConfig.appId] ?? 0)
            : 0;

          const isDraft = isDraftApp(extensionConfig);
          // Route builder apps to the builder session for users who can access
          // the builder; staff without the permission (and clients) keep the
          // live-only portal (/apps) view. Prefer the synchronous install
          // appBuilderId (present the moment this row renders, since it comes
          // from the same installs query) over the async useListAppBuildersQuery
          // map, so a permissioned user's link doesn't briefly downgrade to the
          // portal URL on first paint before the app-builders query resolves.
          const builderId = canAccessBuilder
            ? (extensionConfig.appBuilderId ??
              builderIdByModuleId.get(extensionId))
            : undefined;
          const builderPathname = builderId
            ? getBuilderSessionPath(builderId)
            : undefined;
          const builderHref = builderId
            ? getBuilderSessionPath(builderId, { editor: isDraft })
            : undefined;
          const path = builderHref ?? extensionItemPath;
          // pathname excludes the ?tab query string, so a single match here
          // intentionally highlights the sidebar item for both Editor and Live
          // tabs of the builder page.
          const isBuilderSelected =
            builderPathname !== undefined &&
            (pathname === builderPathname ||
              pathname.startsWith(`${builderPathname}/`));

          return {
            htmlId: `${appNavItemIdPrefix}${extensionId}`,
            label: extensionConfig.name,
            isNonCoreApp: true,
            isExternalLink:
              !builderHref &&
              viewExtensionLocation === ExtensionItemViewLocation.NEW_TAB,
            path,
            iconName: extensionConfig.icon,
            canAccess: true,
            children: isDraft ? (
              enableNewSidebar ? (
                <SidebarNotificationBadge
                  label="Draft"
                  backgroundColor={backgroundColor}
                  textColor={textColor}
                />
              ) : (
                <DraftBadge isActive={isBuilderSelected} className="ml-auto" />
              )
            ) : !extensionConfig.appId ? null : (
              <SidebarNotificationBadge
                showZero={false}
                content={notificationCount}
                backgroundColor={backgroundColor}
                textColor={textColor}
              />
            ),
            notificationCount: isDraft ? 0 : notificationCount,
            selected: isBuilderSelected || queryIdParam === extensionId,
            disabled: planStatus.isBasicPlan,
            tooltipTitle: planStatus.isBasicPlan ? (
              <ExtensionTooltipTitle />
            ) : null,
            queryParams: extensionConfig.queryParams,
          };
        })
    : [];

  const crmNavItem = {
    htmlId: 'sidebar-crm',
    label: Constants.CRM_CLIENTS_PAGE.label,
    path: getCrmPath(),
    icon: Constants.CRM_CLIENTS_PAGE.icon,
    iconName: 'user-group',
    canAccess: routeAccess.allowCRMPages,
    selected:
      pathname.includes('/companies') ||
      pathname.includes('/clients') ||
      pathname.includes('/import'),
  };

  const inboxNavItem = {
    htmlId: 'sidebar-inbox',
    // when other entities invoices notifications are shown in inbox
    // then the inbox page sidebar item label should show 'Notifications'
    // rather than 'Files notifications'
    label: Constants.INBOX_PAGE.label,
    path: Constants.INBOX_PAGE.path,
    icon: Constants.INBOX_PAGE.icon,
    iconName: 'bell',
    children: <InboxNotificationBadge />,
    canAccess: routeAccess.allowInboxPage,
    selected: pathname.includes(Constants.INBOX_PAGE.path),
  };

  const homeNavItem = {
    htmlId: 'sidebar-home',
    label: Constants.HOME_PAGE.label,
    path: Constants.HOME_PAGE.path,
    icon: Constants.HOME_PAGE.icon,
    iconName: 'Compass',
    canAccess: routeAccess.allowHomePage,
    // home sidebar item is selected when user routes to home page
    // or trying to access a specific portal e.g. `/portal/${portalId}`
    selected:
      pathname === Constants.HOME_PAGE.path ||
      pathname.startsWith(Constants.PORTAL_PATH),
  };

  // Classic-only: Studio users access automations via the settings sidebar instead
  const automationNavItem = {
    htmlId: 'sidebar-automations',
    label: Constants.AUTOMATIONS_PAGE.label,
    path: `${Constants.AUTOMATIONS_PAGE.path}`,
    icon: Constants.AUTOMATIONS_PAGE.icon,
    canAccess: !isClient && routeAccess.allowAutomationsPage,
    iconName: 'Automation',
    selected: pathname.includes(Constants.AUTOMATIONS_PAGE.path),
  };

  const defaultPortalSectionNavItems: NavItem[] = React.useMemo(
    () => [
      {
        htmlId: 'sidebar-messages',
        label: Constants.MESSAGES_PAGE.label,
        path: Constants.MESSAGES_PAGE.path,
        icon: Constants.MESSAGES_PAGE.icon,
        canAccess: routeAccess.allowMessagesPage,
        children:
          messages < 1 ? null : (
            <SidebarNotificationBadge
              htmlId="messages-notification-badge"
              showZero={false}
              content={messages}
              backgroundColor={backgroundColor}
              textColor={textColor}
            />
          ),
        notificationCount: messages,
      },
      {
        htmlId: 'sidebar-files',
        label: Constants.FILES_PAGE.label,
        path: Constants.FILES_PAGE.path,
        icon: Constants.FILES_PAGE.icon,
        canAccess: routeAccess.allowFilesPage,
        children:
          // When files notification center flag is turned on
          // don't show files sidebar items notification count badge( internal user only )
          !isClient ? null : (
            <SidebarNotificationBadge
              showZero={false}
              content={files}
              backgroundColor={backgroundColor}
              textColor={textColor}
            />
          ),
        notificationCount: files,
        selected: isFilesNavItemSelected(pathname),
      },
      {
        htmlId: 'sidebar-contracts',
        label: Constants.CONTRACTS_PAGE.label,
        path: `${Constants.CONTRACTS_PAGE.path}?view=${ContractPageTabs.Templates}`,
        icon: Constants.CONTRACTS_PAGE.icon,
        canAccess: routeAccess.allowContractsPage,
        selected: pathname.startsWith(Constants.CONTRACTS_PAGE.path),
        children: (
          <SidebarNotificationBadge
            showZero={false}
            content={contracts}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: contracts,
      },
      {
        htmlId: 'sidebar-forms',
        label: Constants.FORMS_PAGE.label,
        path: Constants.FORMS_PAGE.path,
        icon: Constants.FORMS_PAGE.icon,
        canAccess: isClient
          ? routeAccess.allowFormsClient
          : routeAccess.allowFormsInternal,
        children: !isClient ? null : (
          <SidebarNotificationBadge
            showZero={false}
            content={forms}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: forms,
        selected: pathname.startsWith(Constants.FORMS_PAGE.path),
      },
      !isClient
        ? {
            htmlId: 'sidebar-payments',
            label: Constants.PAYMENT_PAGE.label,
            path: Constants.PAYMENT_PAGE.path,
            icon: Constants.PAYMENT_PAGE.icon,
            canAccess: routeAccess.allowPaymentsPage,
          }
        : {
            htmlId: 'sidebar-payments',
            label: 'Billing',
            path: !isClient
              ? Constants.PRODUCTS_PAGE.path
              : Constants.INVOICES_PAGE.path,
            icon: Constants.INVOICES_PAGE.icon,
            canAccess:
              routeAccess.allowPaymentsPage &&
              routeAccess.allowSubscriptionPage,
            shouldCollapse:
              pathname === Constants.INVOICES_PAGE.path ||
              pathname === Constants.SUBSCRIPTION_PAGE.path,
            subItems: [
              ...(!isClient
                ? [
                    {
                      htmlId: 'sidebar-products',
                      label: Constants.PRODUCTS_PAGE.label,
                      path: Constants.PAYMENTS_SERVICES_PAGE.path,
                      canAccess:
                        routeAccess.allowPaymentsPage &&
                        routeAccess.allowSubscriptionPage,
                    },
                  ]
                : []),
              ...[
                {
                  htmlId: 'sidebar-store',
                  label: Constants.STORE_PAGE.label,
                  path: Constants.PAYMENTS_STORE_PAGE.path,
                  iconName: 'Store',
                  canAccess:
                    routeAccess.allowProductStore &&
                    routeAccess.allowSubscriptionPage,
                },
              ],
              {
                htmlId: 'sidebar-invoices',
                label: Constants.INVOICES_PAGE.label,
                path: Constants.PAYMENTS_INVOICES_PAGE.path,
                canAccess:
                  routeAccess.allowInvoices &&
                  routeAccess.allowSubscriptionPage,
              },
              {
                htmlId: 'sidebar-subscriptions',
                label: Constants.SUBSCRIPTION_PAGE.label,
                path: Constants.PAYMENTS_SUBSCRIPTIONS_PAGE.path,
                canAccess:
                  routeAccess.allowPaymentsPage &&
                  routeAccess.allowSubscriptionPage,
              },
              {
                htmlId: 'sidebar-payment-links',
                label: Constants.PAYMENT_LINKS_PAGE.label,
                path: Constants.PAYMENTS_PAYMENT_LINKS_PAGE.path,
                canAccess:
                  routeAccess.allowPaymentsPage &&
                  routeAccess.allowSubscriptionPage,
              },
            ],
          },
      {
        htmlId: 'sidebar-client-store',
        label: store?.displayName || Constants.STORE_PAGE.label,
        path: Constants.STORE_PAGE.path,
        icon: Constants.STORE_PAGE.icon,
        iconName: 'Store',
        canAccess:
          routeAccess.allowPaymentsPage &&
          routeAccess.allowClientProductStore &&
          Boolean(store),
      },
      {
        htmlId: 'sidebar-payments', // payments for clients
        label: 'Billing',
        path: Constants.CLIENT_BILLING_PAGE.path,
        icon: Constants.CLIENT_BILLING_PAGE.icon,
        canAccess:
          routeAccess.allowClientPaymentsPage &&
          !routeAccess.allowSubscriptionPage,
        children: (
          <SidebarNotificationBadge
            showZero={false}
            content={invoices}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: invoices,
      },
      {
        htmlId: 'sidebar-knowledgeBase',
        label: Constants.HELPDESK_PAGE.label,
        path: Constants.HELPDESK_PAGE.path,
        icon: Constants.HELPDESK_PAGE.icon,
        canAccess: routeAccess.allowKnowledgeBasePage,
        selected: pathname.includes(Constants.HELPDESK_PAGE.path),
      },
    ],
    [routeAccess],
  );

  const getPortalNavItems = () => [
    {
      htmlId: 'sidebar-modules-settings',
      label: APP_SETUP_PAGE.label,
      path: APP_SETUP_PAGE.path,
      canAccess: routeAccess.allowSettingsModuleManagement,
      icon: APP_SETUP_PAGE.icon,
      iconName: 'sliders',
      selected:
        pathname.includes(APP_SETUP_PAGE.path) &&
        !pathname.includes('/app-setup/build'),
    },
    {
      htmlId: 'sidebar-portal-customization',
      label: CUSTOMIZATION_PAGE.label,
      path: '/customization',
      icon: PORTAL_ITEM.icon,
      iconName: 'Customization',
      canAccess: routeAccess.allowSettingsCustomizationPage,
    },
  ];

  /**
   * This function determines portal section nav items
   * from modules settings.
   * @returns
   */
  const getCoreModulesFromModuleSettings = (): CoreModulesNavItems => {
    // when module settings is not defined return the default portal sections
    // navigation items.
    const modules = moduleSettings || [];

    // When a new extension is created, it is added to the extensionsSettings and thus to be taken account of in sidebar

    let allNavItems = defaultPortalSectionNavItems;

    allNavItems = allNavItems.concat(extensionNavItems);

    const clientNavItems = modules
      .map((module): NavItem | null => {
        if (module.disabled) {
          return null;
        }

        if (module.type === 'folder')
          return {
            htmlId: `sidebar-folder-${module.id}`,
            label: module.label,
            iconName: module.icon,
            path: '',
            id: module.id,
            isFolder: true,
            subItems: [],
            isHidden: !isClient && module.iuSidebarHidden,
          };

        const moduleNavItem = allNavItems.find((item) => {
          const itemId = item.htmlId.includes(appNavItemIdPrefix)
            ? item.htmlId.replace(appNavItemIdPrefix, '')
            : item.htmlId.replace(moduleNavItemIdPrefix, '');
          return itemId === module.id && item.canAccess;
        });

        if (moduleNavItem) {
          const navItemIconName =
            module.icon ||
            getModuleIcon(module.type, module.id, extensionsSettings);
          const navItemLabel =
            module.label ||
            getModuleName(module.type, module.id, extensionsSettings);

          const navItem: NavItem = {
            ...moduleNavItem,
            iconName: navItemIconName,
            label: navItemLabel || '',
            id: module.id,
          };
          navItem.isHidden = !isClient && module.iuSidebarHidden;
          if (module.path) {
            navItem.folderPath = module.path;
          }

          return navItem;
        }
        return null;
      })
      .filter(Boolean);

    if (
      isClient &&
      routeAccess.allowPaymentsPage &&
      routeAccess.allowClientProductStore
    ) {
      const storeNavItem = defaultPortalSectionNavItems.find(
        (item) => item.htmlId === 'sidebar-client-store',
      );

      const billingIndexInClient = clientNavItems.findIndex(
        (item) => item.htmlId === 'sidebar-payments',
      );

      if (storeNavItem && billingIndexInClient !== -1) {
        const billingNavItem = clientNavItems[billingIndexInClient];
        // Inherit folderPath from the billing nav item so the store
        // gets nested in the same folder as billing for clients
        const storeNavItemWithFolder = billingNavItem?.folderPath
          ? { ...storeNavItem, folderPath: billingNavItem.folderPath }
          : storeNavItem;
        clientNavItems.splice(billingIndexInClient, 0, storeNavItemWithFolder);
      }
    }

    // move the contents of folders into their folder's subItems
    const navItemsWithFolders = nestItemsInFolders(clientNavItems);

    // Set shouldCollapse for folders based on current path
    navItemsWithFolders.forEach((item) => {
      if (item.isFolder && item.subItems) {
        item.shouldCollapse = item.subItems.some(
          (x) => x.selected || x.path === `${pathname}?id=${queryIdParam}`,
        );
      }
    });

    // folders should be hidden if all of their children are hidden
    navItemsWithFolders.forEach((item) => {
      if (
        item.isFolder &&
        !item.isHidden &&
        item.subItems &&
        !item.subItems.some((subItem) => !subItem.isHidden)
      ) {
        item.isHidden = true;
      }
    });

    // remove all folders in the navItems array
    const navItems = clientNavItems.reduce<NavItem[]>((acc, currItem) => {
      if (currItem.isFolder) {
        return acc;
      }
      return [...acc, currItem];
    }, []);

    return {
      navItems,
      navItemsWithFolders,
    };
  };

  // ── Shared nav items used by both sidebar variants ──

  const coreModulesNavItems: CoreModulesNavItems = React.useMemo(
    () => getCoreModulesFromModuleSettings(),
    [
      moduleSettings,
      isClient,
      extensionNavItems,
      extensionsSettings,
      planStatus,
    ],
  );

  const portalNavItems = React.useMemo(
    () => getPortalNavItems(),
    [routeAccess, isClient, pathname, EnableAppBuilder],
  );

  const clientExperienceItems = getClientExperienceItems({
    routeAccess,
    pathname,
    planStatus,
  });

  const auditLogNavItem: NavItem = {
    htmlId: 'audit-log-setting-page',
    label: SETTINGS_EVENTS_PAGE.label,
    path: SETTINGS_EVENTS_PAGE.path,
    icon: SETTINGS_EVENTS_PAGE.icon,
    iconName: 'AuditLog',
    isHidden: !EnableAuditLogs,
    canAccess: routeAccess.allowSettingsAuditLog,
    children: (
      <UpgradeBadge
        show={!planStatus.isEnterprisePlan && !planStatus.isAdvancedPlan}
      />
    ),
  };

  // ── Classic sidebar: used when EnableStudioSidebar is off ──

  const getClassicSections = () => {
    const mainNavItems = [
      homeNavItem,
      crmNavItem,
      inboxNavItem,
      ...(hasAutomationsAccess ? [automationNavItem] : []),
    ];

    const workspaceNavItems: Array<NavItem> = [
      {
        htmlId: 'main-setting-page',
        label: Constants.GENERAL_SETTINGS_PAGE.label,
        path: Constants.GENERAL_SETTINGS_PAGE.path,
        icon: Constants.GENERAL_SETTINGS_PAGE.icon,
        canAccess: routeAccess.allowSettingsMainPage,
        selected: isGeneralSettingsNavItemSelected(pathname),
        iconName: 'Puzzle',
      },
      {
        htmlId: 'assistant-settings-page',
        label: ASSISTANT_SETTINGS_PAGE.label,
        path: ASSISTANT_SETTINGS_PAGE.path,
        icon: ASSISTANT_SETTINGS_PAGE.icon,
        iconName: 'AssemblyLogo',
        canAccess: routeAccess.allowSettingsAssistant,
        selected: pathname.includes(ASSISTANT_SETTINGS_PAGE.path),
      },
      {
        htmlId: 'members-setting-page',
        label: Constants.SETTINGS_TEAM_PAGE.label,
        path: Constants.SETTINGS_TEAM_PAGE.path,
        icon: Constants.SETTINGS_TEAM_PAGE.icon,
        iconName: 'Teams',
        canAccess: routeAccess.allowSettingsMembersPage,
        children: (
          <UpgradeBadge
            show={
              hasModifyInternalRolePermission &&
              planStatus.requireAdvancedUpgrade
            }
          />
        ),
      },
      auditLogNavItem,
      {
        htmlId: 'payments-setting-page',
        label: PAYMENTS_PAGE.label,
        path: PAYMENTS_PAGE.path,
        icon: PAYMENTS_PAGE.icon,
        iconName: 'credit-card',
        canAccess: routeAccess.allowSettingsModulePayments,
      },
      {
        htmlId: 'api-settings-page',
        label: API_SETTINGS_PAGE.label,
        path: API_SETTINGS_PAGE.path,
        icon: API_SETTINGS_PAGE.icon,
        iconName: 'microchip',
        canAccess: routeAccess.allowSettingsApiPage,
        children: (
          <UpgradeBadge
            show={planStatus.isStarterPlan || planStatus.isTrialing}
          />
        ),
        selected: pathname.includes(API_SETTINGS_PAGE.path),
      },
      {
        htmlId: 'connections-setting-page',
        label: CONNECTIONS_PAGE.label,
        path: CONNECTIONS_PAGE.path,
        icon: CONNECTIONS_PAGE.icon,
        iconName: 'link',
        canAccess: routeAccess.allowSettingsConnectionsPage,
        selected: pathname.includes(CONNECTIONS_PAGE.path),
      },
      {
        htmlId: 'billing-setting-page',
        label: BILLING_PAGE.label,
        path: BILLING_PAGE.path,
        icon: BILLING_PAGE.icon,
        iconName: 'credit-card',
        canAccess: routeAccess.allowSettingsBillingPage,
      },
      {
        htmlId: 'plan-setting-page',
        label: PLAN_PAGE.label,
        path: PLAN_PAGE.path,
        icon: PLAN_PAGE.icon,
        iconName: 'PlansPayments',
        canAccess: routeAccess.allowSettingsPlanPage,
        selected: pathname.includes(PLAN_PAGE.path),
      },
    ];

    const inAccessibleNavItemsForClient: Array<NavItem['label']> = [
      Constants.GENERAL_SETTINGS_PAGE.label,
      Constants.SETTINGS_TEAM_PAGE.label,
    ];

    const filteredWorkspaceNavItems = shouldFilterNonClientItems
      ? workspaceNavItems.filter(
          (x) => !inAccessibleNavItemsForClient.includes(x.label),
        )
      : workspaceNavItems;

    const main: Array<NavSection> = [
      {
        htmlId: 'section-core',
        title: '',
        navItems: mainNavItems,
        canAccess: mainNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-modules',
        title: 'Apps',
        navItems: coreModulesNavItems.navItems,
        canAccess: coreModulesNavItems.navItems.some((item) => item.canAccess),
        navItemsWithFolders: coreModulesNavItems.navItemsWithFolders,
      },
      {
        htmlId: 'section-portal',
        title: 'Preferences',
        navItems: portalNavItems,
        canAccess: portalNavItems.some((x) => x.canAccess),
      },
    ];

    const settings: Array<NavSection> = [
      {
        htmlId: 'section-about-settings',
        title: 'Me',
        navItems: meSectionNavItems,
        canAccess: meSectionNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-workspace-settings',
        title: 'Workspace',
        navItems: filteredWorkspaceNavItems,
        canAccess: filteredWorkspaceNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-client-experience',
        title: t(($) => $.navigation.client_experience),
        navItems: clientExperienceItems,
        canAccess: clientExperienceItems.some((item) => item.canAccess),
      },
    ];

    return { main, settings };
  };

  // ── Studio sidebar: used when EnableStudioSidebar is on ──

  const getStudioSections = () => {
    const teamNavItem: NavItem = {
      htmlId: 'sidebar-team',
      label: Constants.SETTINGS_TEAM_PAGE.label,
      path: '/team',
      icon: Constants.SETTINGS_TEAM_PAGE.icon,
      iconName: 'Teams',
      canAccess: !isClient && routeAccess.allowSettingsMembersPage,
      children: (
        <UpgradeBadge
          show={
            hasModifyInternalRolePermission && planStatus.requireAdvancedUpgrade
          }
        />
      ),
    };

    const coreNavItems = [crmNavItem, teamNavItem, inboxNavItem];

    const customizeNavItems: Array<NavItem> = [
      {
        htmlId: 'sidebar-portal-customization',
        label: 'Brand',
        path: '/brand',
        icon: PORTAL_ITEM.icon,
        iconName: 'Customization',
        canAccess: routeAccess.allowSettingsCustomizationPage,
      },
      {
        htmlId: 'sidebar-modules-settings',
        label: 'Apps',
        path: APP_SETUP_PAGE.path,
        canAccess: routeAccess.allowSettingsModuleManagement,
        icon: APP_SETUP_PAGE.icon,
        iconName: 'sliders',
        selected:
          pathname.includes(APP_SETUP_PAGE.path) &&
          !pathname.includes('/app-setup/build'),
      },
    ];

    const workspaceNavItems: Array<NavItem> = [
      {
        htmlId: 'main-setting-page',
        label: Constants.GENERAL_SETTINGS_PAGE.label,
        path: Constants.GENERAL_SETTINGS_PAGE.path,
        icon: Constants.GENERAL_SETTINGS_PAGE.icon,
        canAccess: routeAccess.allowSettingsMainPage,
        selected: isGeneralSettingsNavItemSelected(pathname),
        iconName: 'Puzzle',
      },
      {
        htmlId: 'assistant-settings-page',
        label: ASSISTANT_SETTINGS_PAGE.label,
        path: ASSISTANT_SETTINGS_PAGE.path,
        icon: ASSISTANT_SETTINGS_PAGE.icon,
        iconName: 'AssemblyLogo',
        canAccess: routeAccess.allowSettingsAssistant,
        selected: pathname.includes(ASSISTANT_SETTINGS_PAGE.path),
      },
      {
        htmlId: 'automations-settings-page',
        label: Constants.AUTOMATIONS_PAGE.label,
        path: SETTINGS_AUTOMATIONS_PATH,
        icon: Constants.AUTOMATIONS_PAGE.icon,
        iconName: 'Automation',
        canAccess: !isClient && hasAutomationsAccess,
        selected: pathname.includes(SETTINGS_AUTOMATIONS_PATH),
      },
      auditLogNavItem,
      {
        htmlId: 'billing-setting-page',
        label: BILLING_PAGE.label,
        path: BILLING_PAGE.path,
        icon: BILLING_PAGE.icon,
        iconName: 'credit-card',
        canAccess: routeAccess.allowSettingsBillingPage,
      },
    ];

    const developersNavItems: Array<NavItem> = [
      {
        htmlId: 'api-settings-page',
        label: API_SETTINGS_PAGE.label,
        path: API_SETTINGS_PAGE.path,
        icon: API_SETTINGS_PAGE.icon,
        iconName: 'microchip',
        canAccess: routeAccess.allowSettingsApiPage,
        children: (
          <UpgradeBadge
            show={planStatus.isStarterPlan || planStatus.isTrialing}
          />
        ),
        selected: pathname.includes(API_SETTINGS_PAGE.path),
      },
      {
        htmlId: 'connections-setting-page',
        label: CONNECTIONS_PAGE.label,
        path: CONNECTIONS_PAGE.path,
        icon: CONNECTIONS_PAGE.icon,
        iconName: 'link',
        canAccess: routeAccess.allowSettingsConnectionsPage,
        selected: pathname.includes(CONNECTIONS_PAGE.path),
      },
    ];

    const billingNavItems: Array<NavItem> = [
      {
        htmlId: 'plan-setting-page',
        label: PLAN_PAGE.label,
        path: PLAN_PAGE.path,
        icon: PLAN_PAGE.icon,
        iconName: 'PlansPayments',
        canAccess: routeAccess.allowSettingsPlanPage,
        selected: pathname.includes(PLAN_PAGE.path),
      },
      {
        htmlId: 'payments-setting-page',
        label: PAYMENTS_PAGE.label,
        path: PAYMENTS_PAGE.path,
        icon: PAYMENTS_PAGE.icon,
        iconName: 'credit-card',
        canAccess: routeAccess.allowSettingsModulePayments,
      },
    ];

    const inAccessibleNavItemsForClient: Array<NavItem['label']> = [
      Constants.GENERAL_SETTINGS_PAGE.label,
      Constants.SETTINGS_TEAM_PAGE.label,
    ];

    const filteredWorkspaceNavItems = shouldFilterNonClientItems
      ? workspaceNavItems.filter(
          (x) => !inAccessibleNavItemsForClient.includes(x.label),
        )
      : workspaceNavItems;

    const main: Array<NavSection> = [
      {
        htmlId: 'section-core',
        title: '',
        navItems: coreNavItems,
        canAccess: coreNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-apps',
        title: 'Apps',
        navItems: coreModulesNavItems.navItems,
        canAccess: coreModulesNavItems.navItems.some((item) => item.canAccess),
        navItemsWithFolders: coreModulesNavItems.navItemsWithFolders,
      },
      {
        htmlId: 'section-customize',
        title: 'Customize',
        navItems: customizeNavItems,
        canAccess: customizeNavItems.some((x) => x.canAccess),
      },
    ];

    const settings: Array<NavSection> = [
      {
        htmlId: 'section-about-settings',
        title: 'Me',
        navItems: meSectionNavItems,
        canAccess: meSectionNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-workspace-settings',
        title: 'Workspace',
        navItems: filteredWorkspaceNavItems,
        canAccess: filteredWorkspaceNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-client-experience',
        title: t(($) => $.navigation.client_experience),
        navItems: clientExperienceItems,
        canAccess: clientExperienceItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-developers-settings',
        title: 'Developers',
        navItems: developersNavItems,
        canAccess: developersNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-billing-settings',
        title: 'Billing',
        navItems: billingNavItems,
        canAccess: billingNavItems.some((item) => item.canAccess),
      },
    ];

    return { main, settings };
  };

  // ── Pick the right variant ──

  const { main: mainSidebarSections, settings: settingsSidebarSections } =
    React.useMemo(
      () => (EnableStudioSidebar ? getStudioSections() : getClassicSections()),
      [
        EnableStudioSidebar,
        routeAccess,
        isClient,
        pathname,
        coreModulesNavItems,
        portalNavItems,
        hasAutomationsAccess,
        shouldFilterNonClientItems,
        planStatus,
        hasModifyInternalRolePermission,
        EnableAuditLogs,
      ],
    );

  return {
    sections:
      sidebarType === 'settings'
        ? settingsSidebarSections
        : mainSidebarSections,
    isLoadingInstalls,
  };
};
